import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { CalendarIcon, Check } from 'lucide-react';
import React from 'react';
import { TemplateField } from '../types';

interface FieldRendererProps {
  field: TemplateField;
  value: string | null;
  isEditing: boolean;
  onChange: (fieldId: number, value: string | null) => void;
}

/**
 * Component to render a field based on its type and mode (edit or preview)
 */
export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  isEditing,
  onChange
}) => {
  const fieldValue = value || '';

  if (!isEditing) {
    // Preview mode with consistent, polished styling
    switch (field.field_type) {
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-4 h-4 rounded-sm flex items-center justify-center transition-colors",
              fieldValue === 'true'
                ? "bg-primary text-primary-foreground"
                : "border border-muted-foreground/30 bg-muted/20"
            )}>
              {fieldValue === 'true' && <Check className="h-3 w-3" />}
            </div>
            <span className="text-sm font-medium text-foreground">
              {fieldValue === 'true' ? 'Yes' : 'No'}
            </span>
          </div>
        );
      case 'textarea':
        return fieldValue ? (
          <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed border-l-2 border-primary/20 pl-3">
            {fieldValue}
          </p>
        ) : (
          <span className="text-sm text-muted-foreground/50 italic">No value</span>
        );
      case 'dropdown':
        return fieldValue ? (
          <Badge
            variant="secondary"
            className="font-normal rounded-md px-2 py-0.5 text-xs bg-muted/60 text-foreground/80 border border-border/50 hover:bg-muted/80 transition-colors"
          >
            {fieldValue}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground/50 italic">No selection</span>
        );
      case 'date':
        return fieldValue ? (
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
            {new Date(fieldValue).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground/50 italic">No date selected</span>
        );
      default:
        return fieldValue ? (
          <span className="text-sm font-medium text-foreground">{fieldValue}</span>
        ) : (
          <span className="text-sm text-muted-foreground/50 italic">No value</span>
        );
    }
  } else {
    // Edit mode with clean, modern styling
    switch (field.field_type) {
      case 'text':
        return (
          <Input
            id={`field-${field.id}`}
            value={fieldValue}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.field_name}`}
            className="mt-0.5"
          />
        );
      case 'textarea':
        return (
          <Textarea
            id={`field-${field.id}`}
            value={fieldValue}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.field_name}`}
            rows={3}
            className="mt-0.5"
          />
        );
      case 'number':
        return (
          <Input
            id={`field-${field.id}`}
            type="number"
            value={fieldValue}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.field_name}`}
            className="mt-0.5"
          />
        );
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2 mt-0.5">
            <Checkbox
              id={`field-${field.id}`}
              checked={fieldValue === 'true'}
              onCheckedChange={(checked) => onChange(field.id, checked ? 'true' : 'false')}
            />
            <label
              htmlFor={`field-${field.id}`}
              className="text-sm leading-none"
            >
              {field.placeholder || field.field_name}
            </label>
          </div>
        );
      case 'dropdown': {
        // Handle both string and JSON array formats for options
        let options: string[] = [];

        if (field.options) {
          try {
            if (typeof field.options === 'string') {
              const trimmed = field.options.trim();
              if (trimmed.startsWith('[')) {
                try {
                  const parsed = JSON.parse(trimmed);
                  if (Array.isArray(parsed)) options = parsed;
                } catch (e) {
                  options = trimmed.replace(/[\[\]"]/g, '').split(',').map(s => s.trim());
                }
              } else {
                options = trimmed.split(',').map(opt => opt.trim());
              }
            } else if (Array.isArray(field.options)) {
              options = field.options;
            }
          } catch (error) {
            console.error('Error parsing dropdown options:', error);
            options = [];
          }
        }

        options = Array.from(new Set(options.filter(Boolean)));

        return (
          <Select
            value={fieldValue}
            onValueChange={(val) => onChange(field.id, val)}
          >
            <SelectTrigger className="mt-0.5 h-9 bg-background/50 border-input">
              <SelectValue placeholder={field.placeholder || `Select ${field.field_name}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option: string, idx: number) => (
                <SelectItem key={`${idx}-${option}`} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case 'date':
        return (
          <Input
            id={`field-${field.id}`}
            type="date"
            value={fieldValue}
            onChange={(e) => onChange(field.id, e.target.value)}
            className="mt-0.5"
          />
        );
      default:
        return (
          <Input
            id={`field-${field.id}`}
            value={fieldValue}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.field_name}`}
            className="mt-0.5"
          />
        );
    }
  }
};

export default FieldRenderer;
